import { DateField } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import {
    getCoverableFromAllOnsaCoverables,
    getCoverableIndex
} from '../../../../utils/coverablesUtil';
import { useState } from 'react';
import {
    areLocalDatesEqual,
    calculateAgeDate,
    convertLocalDateDTOToDate
} from '../../../../utils/dateUtil';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import RemoveModal from '../../../../shared_components/RemoveModal/RemoveModal';
import { track } from '../../../../utils/trackingUtil';
import type { DogOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/pet/dogonsa.dto';
import { allCoverablesAtom } from '../../../../recoil/atoms/allCoverablesAtom';
import { getPetBreedsSelector } from '../../../../recoil/selectors/getPetBreedsSelector';

type ValidationStop = 'RemoveModal' | 'None';

const validatePetAge = (date: Date, flavourName: string): ValidationStop => {
    const ageOfPetInYears = calculateAgeDate(date);
    if (ageOfPetInYears >= 7 && flavourName === 'Katteforsikring') {
        return 'RemoveModal';
    }
    return 'None';
};

const PetBirthdateStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const onsaCoverables = useRecoilValue(allCoverablesOnsaAtom);
    const shoppingBasketCoverables = useRecoilValue(allCoverablesAtom);
    const breedList = useRecoilValue(getPetBreedsSelector);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const pets =
        currentProduct.flavourName === 'Hundeforsikring'
            ? onsaCoverables.dogs ?? []
            : onsaCoverables.cats ?? [];

    const coverableIndex = getCoverableIndex(pets, currentProduct.fixedId);
    const currentCoverable = getCoverableFromAllOnsaCoverables(
        onsaCoverables,
        currentProduct.fixedId,
        currentProduct.lobCode
    );
    const coverable = pets[coverableIndex];

    const { dateOfBirth, name = '' } = coverable;
    const convertedDate =
        dateOfBirth?.year && dateOfBirth?.month !== undefined && dateOfBirth?.day
            ? convertLocalDateDTOToDate(dateOfBirth)
            : undefined;
    const [dateIsValid, setDateIsValid] = useState<boolean>(
        convertedDate !== undefined &&
            validatePetAge(convertedDate, currentProduct.flavourName) === 'None'
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [showDuplicateRemoveModal, setShowDuplicateRemoveModal] = useState<boolean>(false);

    const handleBirthdateChange = (date: Date): void => {
        updateCoverableInformation({
            dateOfBirth: {
                day: date.getDate(),
                month: date.getMonth(),
                year: date.getFullYear()
            }
        });
        const validationRes = validatePetAge(date, currentProduct.flavourName);
        setDateIsValid(validationRes === 'None');
        if (validationRes === 'RemoveModal') {
            setShowRemoveModal(true);
        }
    };

    const isPetDuplicate = (): boolean => {
        const petOnsaList = [...(onsaCoverables.dogs ?? []), ...(onsaCoverables.cats ?? [])];
        const petList = [
            ...(shoppingBasketCoverables.dogs ?? []),
            ...(shoppingBasketCoverables.cats ?? [])
        ];
        const currentPet = currentCoverable as DogOnsaDTO;

        const duplicateONSAPet =
            petOnsaList?.find(
                (pet) =>
                    currentPet !== pet &&
                    pet.flavourCode === currentPet.flavourCode &&
                    pet.name === currentPet.name &&
                    pet.breedId === currentPet.breedId &&
                    areLocalDatesEqual(pet.dateOfBirth, currentPet.dateOfBirth)
            ) !== undefined;

        const duplicatePet =
            petList?.find(
                (pet) =>
                    pet.flavourCode === currentPet.flavourCode &&
                    pet.name === currentPet.name &&
                    breedList.find((breed) => breed.name === pet.breed)?.publicId ===
                        currentPet.breedId &&
                    areLocalDatesEqual(pet.dateOfBirth, currentPet.dateOfBirth)
            ) !== undefined;

        return duplicateONSAPet || duplicatePet;
    };

    const onNext = (): void => {
        setIsLoading(true);
        if (isPetDuplicate()) {
            setIsLoading(false);
            setShowDuplicateRemoveModal(true);
            return;
        }

        const updatedCoverable: CoverableInformationToUpdateDTO = {
            [currentProduct.flavourName === 'Hundeforsikring' ? 'dog' : 'cat']: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                dateOfBirth: coverable.dateOfBirth
            },
            productToUpdate: currentProduct.flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(() => {
            setIsLoading(false);
            const petType = currentProduct.flavourName === 'Hundeforsikring' ? 'Dog' : 'Cat';
            track(`${coverable.flavourName}CoverableInformationBirthDateOf${petType}`, [
                { dateOfBirth: coverable.dateOfBirth }
            ]);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={!dateOfBirth || isLoading || !dateIsValid}
            isNextLoading={isLoading}
            disablePrevious={isLoading}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-PET-COVERABLE-INFORMATION-DOB-QUESTION', {
                    name: name ?? ''
                })}
            >
                <DateField
                    label="Fødselsdato"
                    dateFormat="dd-MM-yyyy"
                    selected={convertedDate}
                    onChange={handleBirthdateChange}
                    maxDate={new Date()}
                    required
                    disabled={isLoading}
                />
                <p className="u-color-secondary-4-medium paragraph--small">Fx 20-01-2023</p>
            </CoverableDetailsTemplate>
            {showDuplicateRemoveModal && currentCoverable && (
                <RemoveModal
                    coverable={currentCoverable}
                    isRemoveAndContinueModal
                    showModal={showDuplicateRemoveModal}
                    closeModal={() => {
                        setShowDuplicateRemoveModal(false);
                    }}
                />
            )}
            {showRemoveModal && currentCoverable && (
                <RemoveModal
                    coverable={currentCoverable}
                    title={getHippoText('DSS-REMOVE-CONTINUE-MODAL-OLDER-PET-HEADER', {
                        pet_name: currentCoverable?.name ?? ''
                    })}
                    description={getHippoText('DSS-REMOVE-CONTINUE-MODAL-OLDER-PET-SUBHEADER')}
                    isRemoveAndContinueModal
                    showModal={showRemoveModal}
                    closeModal={() => {
                        setShowRemoveModal(false);
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default PetBirthdateStep;
